import { PERMISSIONS } from "@procuremate/shared";
import { clsx } from "clsx";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet } from "react-router";
import { useHasPermission } from "@/app/require-permission";

/** Administration area: section tabs (users, organisation, directory, imports, suppliers, usage) above the page. */
export function AdminLayout() {
  const { t } = useTranslation();
  const canImport = useHasPermission(PERMISSIONS.IMPORTS_MANAGE);
  const canSeeUsage = useHasPermission(PERMISSIONS.USAGE_READ);
  const tab = ({ isActive }: { isActive: boolean }) =>
    clsx(
      "-mb-px border-b-2 px-1 pb-2 text-sm font-semibold",
      isActive ? "border-primary text-primary" : "border-transparent text-fg-muted hover:text-primary",
    );
  return (
    <div className="flex flex-col gap-6">
      <nav aria-label={t("admin.sections")} className="flex flex-wrap gap-x-6 gap-y-1 border-b border-border">
        <NavLink to="/admin/users" className={tab}>
          {t("admin.users.title")}
        </NavLink>
        <NavLink to="/admin/org" className={tab}>
          {t("admin.org.title")}
        </NavLink>
        {canImport ? (
          <>
            <NavLink to="/admin/directory" className={tab}>
              {t("admin.directory.title")}
            </NavLink>
            <NavLink to="/admin/imports" className={tab}>
              {t("admin.imports.tab")}
            </NavLink>
            <NavLink to="/admin/suppliers" className={tab}>
              {t("admin.suppliers.tab")}
            </NavLink>
          </>
        ) : null}
        {canSeeUsage ? (
          <NavLink to="/admin/usage" className={tab}>
            {t("admin.usage.tab")}
          </NavLink>
        ) : null}
      </nav>
      <Outlet />
    </div>
  );
}

/** White card used for every admin section. */
export function Card({
  title,
  children,
  actions,
  headingLevel = 2,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  headingLevel?: 1 | 2;
}) {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  return (
    <section className="rounded-lg bg-surface p-5 shadow-card sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Heading className={clsx("font-bold text-primary", headingLevel === 1 ? "text-2xl" : "text-lg")}>
          {title}
        </Heading>
        {actions}
      </div>
      {children}
    </section>
  );
}
